import { supabase } from "@/integrations/supabase/client";
import { connectorActionError } from "@/lib/connectorActionError";
import { connectedFileReadSessionId } from "@/lib/connectedFileRead";
import { connectedTaskUuid } from "@/lib/connectedTaskWriteProof";

export * from "@/lib/connectedTaskWriteProof";

const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
export async function connectedTaskOwnerSession(owner: string, expectedSid?: string) {
  const user = await supabase.auth.getUser();
  const active = await supabase.auth.getSession();
  const session = active.data.session;
  const sid = session?.access_token ? connectedFileReadSessionId(session.access_token) : null;
  if (!connectedTaskUuid(owner) || user.error || user.data.user?.id !== owner || active.error || session?.user.id !== owner ||
    !sid || expectedSid && sid !== expectedSid) throw new Error("invalid_session");
  return { token: session.access_token, sid };
}
/** Pin the login before and after IO, including re-login by the same owner. */
export async function invokeConnectedTaskWrite(owner: string, sid: string, body: Record<string, unknown>, options: {
  signal: AbortSignal; isCurrent: () => boolean; surfaceQuota?: (error: unknown) => Promise<boolean>;
}): Promise<unknown> {
  const before = await connectedTaskOwnerSession(owner, sid);
  if (!options.isCurrent() || options.signal.aborted) throw new Error("invalid_session");
  let changed = false;
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    if (session?.user.id !== owner || connectedFileReadSessionId(session.access_token) !== sid) changed = true;
  });
  try {
    const response = await supabase.functions.invoke("expansion-executor", {
      headers: { Authorization: "Bearer " + before.token }, body, signal: options.signal,
    });
    await connectedTaskOwnerSession(owner, sid);
    if (!options.isCurrent() || changed || options.signal.aborted) throw new Error("invalid_session");
    if (response.error) {
      if (options.surfaceQuota && await options.surfaceQuota(response.error)) throw new Error("__quota_surfaced__");
      throw await connectorActionError(response.error);
    }
    if (!object(response.data) || response.data.error && body.phase !== "receipt_status") throw new Error(object(response.data) && typeof response.data.error === "string" ? response.data.error : "outcome_unknown");
    return response.data;
  } finally { subscription.unsubscribe(); }
}
